'use client';
import React, { useEffect, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { CalendarManager, IPOs } from '@benzinga/calendar-manager';
import { DateTime } from 'luxon';
import { formatDatePretty } from '@benzinga/date-utils';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { TickerCell, TextCell } from './TableCells';
import type { ColumnsType } from 'antd/es/table';

const columns: ColumnsType<IPOs> = [
  {
    key: 'ticker',
    title: 'Ticker',
    dataIndex: 'ticker',
    render: (_value, record) => <TickerCell ticker={record.ticker} name={record.name} />,
  },
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    render: value => (
      <TextCell>
        {value ? formatDatePretty(value, { timePeriod: { month: 'short', day: 'numeric', year: 'numeric' } }) : '--'}
      </TextCell>
    ),
  },
  {
    key: 'exchange',
    title: 'Exchange',
    dataIndex: 'exchange',
    render: value => <TextCell>{value}</TextCell>,
  },
  {
    key: 'pricing_date',
    title: 'IPO Date',
    dataIndex: 'pricing_date',
    render: value => (
      <TextCell>
        {value ? formatDatePretty(value, { timePeriod: { month: 'short', day: 'numeric', year: 'numeric' } }) : '--'}
      </TextCell>
    ),
  },
  {
    key: 'price_min',
    title: 'Price Range',
    dataIndex: 'price_min',
    render: (_value, record) => {
      const minPrice = record.price_min
        ? `$${(typeof record.price_min === 'string' ? parseFloat(record.price_min) : record.price_min).toFixed(2)}`
        : '--';
      const maxPrice = record.price_max
        ? `$${(typeof record.price_max === 'string' ? parseFloat(record.price_max) : record.price_max).toFixed(2)}`
        : '--';

      return (
    <>
        <TextCell>
          {minPrice} - {maxPrice}
        </TextCell>
      </>
      );
    },
  },
];

const IPOsModule: React.FC = () => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IPOs[]>([]);

  useEffect(() => {
    const fetchIPOs = async () => {
      try {
        const calendarManager = session.getManager(CalendarManager);
        const response = await calendarManager.getCalendarData(
          'ipos',
          {
            date: DateTime.now().toFormat('yyyy-LL-dd'),
            pageSize: 10,
          },
          true,
        );
        setData(response?.ok ?? []);
      } catch (error) {
        console.error('Error fetching IPOs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIPOs();
  }, [session]);

  return (
    <ModuleWrapper title="Upcoming IPOs">
      <ModuleTable
        columns={columns}
        data={data.map((item, index) => ({ ...item, key: `${item.ticker}-${index}` }))}
        loading={loading}
        emptyText="No upcoming IPOs scheduled"
      />
    </ModuleWrapper>
  );
};  

export default IPOsModule;
